/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useEffect} from 'react';
import TextField, {StandardTextFieldProps} from '@material-ui/core/TextField';
import {GenericObject} from '@ion/core/src/types/entities';
import {errorHelperTextPadding} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useExtendStyles} from '../../../hooks/useExtendStyles';
import Render from '../../../render/Render';

export interface TextFieldTypes {
  onMount?: Function;
  label?: string | ReactNode;
  hasError?: boolean;
  errorText?: string | ReactNode;
  fieldLabel?: string | ReactNode;
  required?: boolean;
  placeholder?: string | ReactNode;
  inputProps?: GenericObject;
  defaultValue?: string | number | unknown;
  autoComplete?: 'on' | 'off' | 'username';
  styleOverrides?: (theme: DLSTheme) => GenericObject;
  style?: object;
  readOnlyText?: boolean;
  maxLength?: number;
}

export type Props = TextFieldTypes & StandardTextFieldProps;

interface InputBaseStyleProps {
  multiline?: boolean;
}

const useStyles = () => ({
  root : {
    width : '100%'
  },
  inputBase : {
    height       : ({multiline}: InputBaseStyleProps) => multiline ? 'auto' : '44px',
    borderRadius : '8px',
    background   : dlsColor('white'),
    boxSizing    : 'border-box',
    padding      : '8px 16px',
    display      : 'flex',
    alignItems   : ({multiline}: InputBaseStyleProps) => multiline ? 'flex-start' : 'center',
    transition   : 'box-shadow 0.15s ease, border-color 0.15s ease',
    '& $input'   : {
      height           : '100%',
      padding          : 0,
      paddingLeft      : '4px',
      fontFamily       : 'Inter',
      fontStyle        : 'normal',
      fontWeight       : 400,
      fontSize         : '14px',
      lineHeight       : '20px',
      color            : dlsColor('grey1'),
      '&::placeholder' : {
        color   : dlsColor('grey3'),
        opacity : 0.65
      },
      '&:-webkit-autofill' : {
        WebkitBoxShadow     : `0 0 0 1000px ${dlsColor('white')} inset`,
        WebkitTextFillColor : dlsColor('grey1'),
        caretColor          : dlsColor('grey1')
      },
      '&:-webkit-autofill:hover' : {
        WebkitBoxShadow     : `0 0 0 1000px ${dlsColor('white')} inset`,
        WebkitTextFillColor : dlsColor('grey1')
      },
      '&:-webkit-autofill:focus' : {
        WebkitBoxShadow     : `0 0 0 1000px ${dlsColor('white')} inset`,
        WebkitTextFillColor : dlsColor('grey1')
      }
    },
    '&:hover $notchedOutline' : {
      borderColor : dlsColor('grey3')
    },
    '&:hover' : {
      background : dlsColor('grey8')
    },
    '&:hover $input' : {
      lineHeight : '20px',
      color      : dlsColor('grey3')
    },
    '&$focused $notchedOutline' : {
      borderColor : dlsColor('darkTeal'),
      borderWidth : '2px'
    },
    '&$focused' : {
      background : dlsColor('white')
    },
    '&$focused $input' : {
      fontSize   : '14px',
      lineHeight : '20px',
      color      : dlsColor('grey1')
    },
    '&$error $notchedOutline' : {
      borderColor : dlsColor('red'),
      borderWidth : '1px'
    },
    '&$error $input' : {
      fontSize   : '14px',
      lineHeight : '20px',
      color      : dlsColor('grey1')
    },
    '&$disabled' : {
      background : dlsColor('grey8')
    },
    '&$disabled $notchedOutline' : {
      borderColor : dlsColor('grey5')
    },
    '&$disabled $input' : {
      fontWeight : 400,
      fontSize   : '14px',
      lineHeight : '20px',
      color      : dlsColor('grey4')
    }
  },
  input          : {},
  focused        : {},
  error          : {},
  disabled       : {},
  notchedOutline : {
    borderColor : dlsColor('grey5'),
    borderWidth : '1px'
  },
  floatingLabel : {
    color         : dlsColor('grey2'),
    fontFamily    : 'Roboto',
    fontStyle     : 'normal',
    fontWeight    : 700,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '0.5px'
  },
  floatingLabelError : {
    color         : dlsColor('red'),
    fontFamily    : 'Roboto',
    fontStyle     : 'normal',
    fontWeight    : 700,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '0.5px'
  },
  floatingLabelDisabled : {
    color : dlsColor('grey4')
  },
  shrink : {
    transform : 'translate(14px, -6px)'
  },
  fieldLabel : {
    color         : dlsColor('grey2'),
    marginBottom  : '8px',
    padding       : '0 8px',
    fontFamily    : 'Roboto',
    fontStyle     : 'normal',
    fontWeight    : 700,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '0.5px'
  },
  fieldLabelFocused : {
    color : dlsColor('aquamarine')
  },
  fieldLabelError : {
    color : dlsColor('red')
  },
  fieldLabelDisabled : {
    color : dlsColor('grey4')
  },
  requiredAsterisk : {
    color  : dlsColor('red'),
    margin : '0 2px'
  },
  helperText : {
    marginTop     : '4px',
    marginLeft    : 0,
    marginRight   : 0,
    padding       : '0 8px',
    fontFamily    : 'Roboto',
    fontStyle     : 'normal',
    fontWeight    : 400,
    fontSize      : '12px',
    lineHeight    : '14px',
    letterSpacing : '0.005em',
    color         : dlsColor('grey2')
  },
  helperTextDisabled : {
    color : dlsColor('grey4')
  },
  helperTextError : {
    color : dlsColor('grey2'),
    ...errorHelperTextPadding
  }
});

/**
 * @TextField
 *
 * Defines the TextField component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   <TextField label='Name'
 *     hasError
 *     errorText='Please enter a valid Name' />
 *
 * ```
 */
const TextFieldWrapper = ({
  onMount,
  label,
  fieldLabel,
  placeholder,
  errorText,
  inputProps,
  style,
  styleOverrides,
  defaultValue,
  disabled = false,
  InputProps,
  hasError = false,
  required = false,
  readOnlyText = false,
  autoComplete = 'on',
  onFocus,
  onBlur,
  FormHelperTextProps,
  variant: _variant,
  multiline,
  ...props
}: Props) => {
  const _styleOverrides = style ? () => (style) : null;
  const classes         = useExtendStyles(useStyles, _styleOverrides || styleOverrides)({disabled, multiline});

  useEffect(() => {
    onMount?.();
  }, []);

  const handleFocus = function(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    onFocus?.(event);
  };

  const handleBlur = function(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    onBlur?.(event);
  };

  const fieldLabelClasses = [
    classes.fieldLabel,
    hasError ? classes.fieldLabelError : '',
    disabled ? classes.fieldLabelDisabled : ''
  ].filter(Boolean).join(' ');

  return (
    <>
      <Render.If condition={!!fieldLabel}>
        <div className={fieldLabelClasses}>
          {fieldLabel}
          {required && <span className={classes.requiredAsterisk}>*</span>}
        </div>
      </Render.If>
      <TextField error={hasError}
        className={classes.root}
        disabled={disabled}
        required={required}
        variant='outlined'
        onFocus={handleFocus}
        onBlur={handleBlur}
        InputProps={{
          classes : {
            root           : classes.inputBase,
            input          : classes.input,
            focused        : classes.focused,
            error          : classes.error,
            disabled       : classes.disabled,
            notchedOutline : classes.notchedOutline
          },
          ...InputProps
        }}
        InputLabelProps={{
          classes : {
            root : hasError
              ? classes.floatingLabelError
              : classes.floatingLabel,
            focused  : 'focused',
            disabled : classes.floatingLabelDisabled,
            shrink   : classes.shrink
          }
        }}
        FormHelperTextProps={{
          classes : {
            root     : classes.helperText,
            disabled : classes.helperTextDisabled,
            error    : classes.helperTextError
          },
          ...FormHelperTextProps
        }}
        inputProps={{placeholder, ...inputProps, readOnly: readOnlyText, autoComplete: autoComplete}}
        label={!placeholder && label}
        defaultValue={defaultValue}
        multiline={multiline}
        {...props}
        helperText={hasError ? errorText : props?.helperText} />
    </>
  );
};

export default TextFieldWrapper;
